import { useId, useRef, useState, type KeyboardEvent } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Textarea } from "@/shared/ui/textarea";
import { cn } from "@/shared/lib/utils";

import type { Question } from "../../api/types";
import {
  circled,
  isConsentQuestion,
  optionDisplayLabel,
  toAnswers,
  type QuestionAnswer,
} from "../../model/collect";

/** 文字回答上限(api `AnswerValues.TEXT_MAX` = 2000 字);超了 api 400「回答最多 2000 字」。 */
const ANSWER_TEXT_MAX = 2000;

/**
 * 追问流里的一道题:single(单选 radiogroup + 「下一题」)/ multi(可多选 chip + 「确认」)/ text(输入框 + 「提交」)/
 * confirm(「确认」/「跳过」)。选项带 `source` 的(冲突题)在选项下写「来源:…」。
 * ★ 跳过是本地收起,不发请求 —— 契约里没有「跳过」这条答案,发 confirm:false 只会把字段写空。
 * ★ 授权题(`q_consent:*`)一个都不勾也能「确认」= 都不公开(附录 A.34),卡底下说清没勾的不上站(设计稿屏 3 ③);
 *   别的多选题仍要至少勾一项。选项名去掉 ai 自带的「(✓ 允许公开)」,✓ 只画在勾上的那几个。
 * ★ `submitting` = 这张卡的答案正在路上(转圈);`disabled` = 页面上有任何写口在路上(整张卡锁住,串行提交)。
 * ★ 标题 id 用 `useId`:题号可能带空格 / 中文(附录 A.35②),直接拼进 id 会把 aria-labelledby 拆成几段。
 * ★ 单选按 WAI-ARIA radio 模式走键盘:整组只占一个 Tab 位(roving tabindex —— 选中的那个,没选时第一个);
 *   ←/↑ 上一个、→/↓ 下一个(首尾循环)、Home / End 到头 / 尾,焦点移过去即选中。多选 chip 是各自独立的开关,
 *   照常每个一个 Tab 位。
 */
export function QuestionCard({
  index,
  question,
  submitting,
  disabled,
  onAnswer,
  onSkip,
}: {
  index: number;
  question: Question;
  submitting: boolean;
  disabled: boolean;
  onAnswer: (answer: QuestionAnswer) => void;
  onSkip: () => void;
}) {
  const [picked, setPicked] = useState<string[]>([]);
  const [text, setText] = useState("");
  const titleId = useId();
  const radioRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const consent = isConsentQuestion(question);
  const toggle = (value: string) =>
    setPicked((prev) =>
      question.kind === "multi"
        ? prev.includes(value)
          ? prev.filter((v) => v !== value)
          : [...prev, value]
        : [value]
    );

  const submit = () => onAnswer(toAnswers(question, picked, text));
  const spinner = submitting ? <Loader2 className="animate-spin" /> : null;
  const single = question.kind === "single";
  /** 单选组唯一的 Tab 位:选中的那个;没选时第一个。 */
  const tabStop = Math.max(
    0,
    question.options.findIndex((o) => picked.includes(o.value))
  );

  const onRadioKeyDown = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const n = question.options.length;
    if (disabled || n === 0) return;
    let next: number | null = null;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = (i + 1) % n;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = (i - 1 + n) % n;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = n - 1;
    if (next === null) return;
    e.preventDefault();
    setPicked([question.options[next].value]);
    radioRefs.current[next]?.focus();
  };

  return (
    <article
      aria-labelledby={titleId}
      aria-busy={submitting || undefined}
      className="space-y-3 rounded-lg border border-border bg-card p-4 text-sm"
    >
      <h3 id={titleId} className="font-semibold text-foreground">
        <span className="mr-1 text-primary">{circled(index)}</span>
        {question.text}
      </h3>

      {(single || question.kind === "multi") && (
        <div
          role={single ? "radiogroup" : "group"}
          aria-labelledby={titleId}
          className="flex flex-wrap gap-2"
        >
          {question.options.map((o, i) => {
            const on = picked.includes(o.value);
            return (
              <div key={o.value} className="flex flex-col items-start gap-0.5">
                <button
                  type="button"
                  {...(single
                    ? {
                        role: "radio",
                        "aria-checked": on,
                        tabIndex: i === tabStop ? 0 : -1,
                        ref: (el: HTMLButtonElement | null) => {
                          radioRefs.current[i] = el;
                        },
                        onKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => onRadioKeyDown(e, i),
                      }
                    : { "aria-pressed": on })}
                  disabled={disabled}
                  onClick={() => toggle(o.value)}
                  className={cn(
                    "rounded-full border px-3 py-1 text-xs font-medium transition-colors disabled:opacity-60",
                    on
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-foreground hover:border-primary/50"
                  )}
                >
                  {question.kind === "multi" && on && <span aria-hidden>✓ </span>}
                  {optionDisplayLabel(question, o.label)}
                </button>
                {o.source && (
                  <span className="pl-3 text-2xs text-muted-foreground">来源:{o.source}</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {question.kind === "text" && (
        <Textarea
          aria-label="回答"
          rows={3}
          maxLength={ANSWER_TEXT_MAX}
          value={text}
          disabled={disabled}
          onChange={(e) => setText(e.target.value)}
          placeholder="直接写在这里"
        />
      )}

      {consent && <p className="text-2xs text-muted-foreground">未勾选的不会出现在官网。</p>}

      <div className="flex flex-wrap items-center gap-2">
        {single && (
          <Button size="sm" disabled={disabled || picked.length === 0} onClick={submit}>
            {spinner}
            下一题
          </Button>
        )}
        {question.kind === "multi" && (
          <Button
            size="sm"
            disabled={disabled || (picked.length === 0 && !consent)}
            onClick={submit}
          >
            {spinner}
            确认
          </Button>
        )}
        {question.kind === "text" && (
          <Button size="sm" disabled={disabled || text.trim() === ""} onClick={submit}>
            {spinner}
            提交
          </Button>
        )}
        {question.kind === "confirm" && (
          <>
            <Button size="sm" disabled={disabled} onClick={submit}>
              {spinner}
              确认
            </Button>
            <Button size="sm" variant="outline" disabled={disabled} onClick={onSkip}>
              跳过
            </Button>
          </>
        )}
      </div>
    </article>
  );
}
